import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Curve3 } from "@babylonjs/core/Maths/math.path";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreateTube } from "@babylonjs/core/Meshes/Builders/tubeBuilder";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { CreateCylinder } from "@babylonjs/core/Meshes/Builders/cylinderBuilder";
import { CreateLines, CreateLineSystem } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import { Vector2 } from "@babylonjs/core/Maths/math.vector";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { ElementGeometry } from "./ElementGeometry";
import { RoomManager, InteractorMode } from './../../RoomManager';
import { Util } from "../../utils/Util";
import { clamp, EPSILON } from "@egr/wcf/modules/utils/math";
import { ShadowGenerator } from "@egr/wcf/modules/core/rendering/ShadowGenerator";
import { CoordinatesType } from "@egr/wcf/modules/core/view/View";
import { angleBetweenVectors3, signedAngleBetweenVectors2, VECTOR2_Y } from "@egr/wcf/modules/core/utils/MathUtils";
import { ElementChangedFlags } from '@egr/wcf/modules/core/mdl';
import { PropertyValue, BoolProperty, LengthProperty, StringProperty, PropertyChangedResult } from '@egr/wcf/modules/core/prop';
export class CameraPath extends ElementGeometry {
    constructor(pRoomManager, pElement) {
        super(pRoomManager, pElement);
        this.mBoxPos = 0;
        this.mDuration = 10;
        this.mRoomElement = pElement;
        this.mRoomManager = pRoomManager;
        this.mPoints = new Array();
        this.mMidPoints = new Array();
        this.mCutPoints = new Array();
        this.mLinePoints = new Array();
        this.mLinePoints_ori = new Array();
        this.mLinePoints2dProj = new Array();
        this.mWidth = 0;
        this.mDepth = 0;
        this.mMaterial = -1;
        this.mColor = "#999999";
        this.mCenter = Vector3.Zero();
        this.mminPoint = Vector3.Zero();
        this.mmaxPoint = Vector3.Zero();
        this.mArea = 0;
        this.mPerimeter = 0;
        this.mClosedPolygon = false;
        this.mCatMullRom = new Curve3([]);
        this.mSplinePrecision = 30;
        this.mRoomElement.Params.ElementPoints = new Array();
        this.mRoomElement.Params.ElementOriginalPoints = new Array();
        this.mRoomElement.Params.material = -1;
        this.mRoomElement.Params.materialColor = this.mColor;
        this.mRoomElement.Params.animationSpeed = 5;
        this.mRoomElement.Params.CamPointTargetsCustom = [];
    }
    getType() {
        return ("CameraPath");
    }
    setMesh(pMesh) {
        this.mArea = Util.calcMeshArea(pMesh) * 0.25;
        this.mPerimeter = this.mRoomManager.calcMeshPerimeter(pMesh);
    }
    get drawnMesh() {
        return (this.mMesh2d);
    }
    getPoints() {
        return (this.mPoints);
    }
    getMidPoints() {
        return (this.mMidPoints);
    }
    getPolygon(pOffset = true) {
        let tpolygonPoints = [];
        let tcamPathPoints = this.mLinePoints;
        for (let tP of tcamPathPoints) {
            let tnodePos = new Vector3(0, 0, 0);
            if (pOffset)
                tnodePos = this.mRoomElement.position.clone();
            let tnP = tP.add(tnodePos);
            tpolygonPoints.push(tnP);
        }
        return (tpolygonPoints);
    }
    getPolygonCenter(pOffset = true) {
        let tpolygonPoints = [];
        let tcamPathPoints = this.mLinePoints;
        for (let tP of tcamPathPoints) {
            let tnodePos = new Vector3(0, 0, 0);
            if (pOffset)
                tnodePos = this.mRoomElement.position.clone();
            let tnP = tP.add(tnodePos);
            tpolygonPoints.push(tnP);
        }
        let tminX = tpolygonPoints[0].x;
        let tminY = tpolygonPoints[0].z;
        let tmaxX = tpolygonPoints[0].x;
        let tmaxY = tpolygonPoints[0].z;
        for (let tP of tpolygonPoints) {
            tminX = Math.min(tP.x, tminX);
            tmaxX = Math.max(tP.x, tmaxX);
            tminY = Math.min(tP.z, tminY);
            tmaxY = Math.max(tP.z, tmaxY);
        }
        let tP1 = new Vector2(tminX, tminY);
        let tP2 = new Vector2(tmaxX, tmaxY);
        let tRes = Vector2.Center(tP1, tP2);
        return (tRes);
    }
    getLinePoints() {
        return this.mLinePoints;
    }
    getShortestLineDistance() {
        let tminDist = 999999;
        for (let i = 0; i < this.mLinePoints.length - 1; i++) {
            const tP0 = this.mLinePoints[i];
            const tP1 = this.mLinePoints[i + 1];
            const tDist = Vector3.Distance(tP0, tP1);
            tminDist = tDist < tminDist ? tDist : tminDist;
        }
        return (tminDist);
    }
    width() {
        return this.mWidth;
    }
    depth() {
        return this.mDepth;
    }
    material() {
        return this.mMaterial;
    }
    center() {
        return this.mCenter;
    }
    currentViewAlongPath() {
        let tRes = false;
        const tcurrTargetDir = this.getCurrTargetDirection();
        const tcurrPathDir = this.getCurrPathDirection();
        tRes = Util.samePointsV3(tcurrTargetDir, tcurrPathDir, EPSILON);
        return (tRes);
    }
    nextViewAlongPath() {
        const tBoxPos = this.getBoxPos();
        const tPOI = this.mRoomElement.Params.CamPointTargetsCalculated[this.mBoxPos];
        const tRes = tPOI.next_Target;
        return (tRes);
    }
    minPoint() {
        return this.mminPoint;
    }
    maxPoint() {
        return this.mmaxPoint;
    }
    showInteractors(pShow) {
        if (this.mInteractors != null)
            this.mInteractors.showInteractors(pShow);
    }
    compute2dProj() {
        let tview = this.mRoomManager.view;
        if (this.mLinePoints.length === 0 || tview == null) {
            return;
        }
        this.mLinePoints2dProj = [];
        for (let tp3d of this.mLinePoints) {
            const ttransP = Vector3.TransformCoordinates(tp3d, this.mRoomElement.transform);
            this.mLinePoints2dProj.push(tview.projectPoint2(ttransP, CoordinatesType.Client));
        }
    }
    getClosestBoxPos2d(pPos) {
        let tRes = 0;
        let tminDist = 999999;
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < this.mLinePoints2dProj.length; i++) {
            const tp2d = this.mLinePoints2dProj[i];
            if (tp2d == null) {
                continue;
            }
            const ttmpDist = Vector2.Distance(pPos, tp2d);
            if (ttmpDist < tminDist) {
                tminDist = ttmpDist;
                tRes = i;
            }
        }
        return (tRes);
    }
    getNearestPosIndex3d(pPos) {
        let tRes = 0;
        let tminDist = 999999;
        const tPos = Vector3.TransformCoordinates(pPos, this.mRoomElement.transformInverted);
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < this.mLinePoints.length; i++) {
            const tp3d = this.mLinePoints[i];
            if (tp3d == null) {
                continue;
            }
            const ttmpDist = Vector3.Distance(tPos, tp3d);
            if (ttmpDist < tminDist) {
                tminDist = ttmpDist;
                tRes = i;
            }
        }
        return (tRes);
    }
    getPosOfIndex(index) {
        const ttmpIdx = clamp(index, 0, this.mLinePoints.length - 1);
        let tPos = this.mLinePoints[ttmpIdx];
        tPos = Vector3.TransformCoordinates(tPos, this.mRoomElement.transform);
        return (tPos);
    }
    getBoxPos(forInteractor = false, pasOffset = false, pIdx) {
        const tOffset = forInteractor ? 0.0 : 0;
        const tPts = this.mLinePoints.slice(); // this.mCatMullRom.getPoints().slice();
        let tRes = Vector3.Zero();
        const tIdxLength = tPts.length - 1;
        let tBoxPosIdx = this.mBoxPos;
        if (pIdx != null) {
            let ttmpIdx = clamp(pIdx, 0, tIdxLength);
            tBoxPosIdx = ttmpIdx;
        }
        if (tBoxPosIdx <= tPts.length - 1) {
            tRes = tPts[tBoxPosIdx].clone();
            if (pasOffset) {
                tRes.y += this.mRoomElement.height;
            }
            return (tRes);
        }
        this.mBoxPos = 0;
        tBoxPosIdx = 0;
        tRes = tPts[tBoxPosIdx].clone();
        tRes.y = tOffset;
        return (tRes);
    }
    getBoxPosIndex() {
        return (this.mBoxPos);
    }
    getTargetPos() {
        const tResCust = this.mRoomElement.Params.CamPointTargetsCalculated[this.mBoxPos];
        return (tResCust.target.clone());
    }
    getStartDirection() {
        let tRes = null;
        if (this.mLinePoints.length < 2) {
            return (null);
        }
        tRes = this.mLinePoints[0].subtract(this.mLinePoints[1]).normalize();
        return (tRes);
    }
    getEndDirection() {
        let tRes = null;
        if (this.mLinePoints.length < 2) {
            return (null);
        }
        const tLen = this.mLinePoints.length - 1;
        tRes = this.mLinePoints[tLen].subtract(this.mLinePoints[tLen - 1]).normalize();
        return (tRes);
    }
    getCurrCustomTarget() {
        let tRes = null;
        for (let tPOI of this.mRoomElement.Params.CamPointTargetsCustom) {
            if (tPOI.index === this.mBoxPos) {
                tRes = tPOI;
                break;
            }
        }
        return (tRes);
    }
    getCurrTargetDirection() {
        let tcurrPos = this.getBoxPos();
        let tcurrTarget = this.mRoomElement.Params.CamPointTargetsCalculated[this.mBoxPos];
        const tcurrTargetTrans = tcurrTarget.target.subtract(this.mRoomElement.node.position);
        const tDir = (tcurrTargetTrans.subtract(tcurrPos)).normalize();
        return (tDir);
    }
    getCurrPathDirection(p2d = false) {
        const tcurrTarget = this.mRoomElement.Params.CamPointTargets[this.mBoxPos];
        let tBoxPos = this.getBoxPos(true);
        tBoxPos = Vector3.TransformCoordinates(tBoxPos, this.mRoomElement.transform);
        const tcurrTargetTrans = (tcurrTarget.subtract(tBoxPos)).normalize();
        if (!p2d) {
            return (tcurrTargetTrans);
        }
        return (Util.to2d(tcurrTargetTrans));
    }
    getCurrPathRad() {
        const tcurrPathDir = this.getCurrPathDirection(true);
        const tdirAngle = signedAngleBetweenVectors2(VECTOR2_Y, tcurrPathDir);
        return (tdirAngle);
    }
    getDuration() {
        return (this.mDuration);
    }
    getCustomTargetPos() {
        let tRes = [];
        for (let tcustTargets of this.mRoomElement.Params.CamPointTargetsCustom) {
            tRes.push(tcustTargets.index);
        }
        return (tRes);
    }
    setBoxPos(position) {
        this.mBoxPos = this.getClosestBoxPos2d(position);
    }
    setBoxPos_2d(position) {
        const tpathP = this.mLinePoints;
        const tpos2d = Util.to2d(position);
        if (tpathP.length < 1) {
            return;
        }
        let tDist = 9999999;
        let tnewPos = 0;
        for (let i = 0; i < tpathP.length; i++) {
            const tP = Util.to2d(tpathP[i]);
            const ttmpDist = Vector2.Distance(tP, tpos2d);
            if (ttmpDist < tDist) {
                tDist = ttmpDist;
                tnewPos = i;
            }
        }
        this.mBoxPos = tnewPos;
    }
    setBoxPosByIndex(pIndex) {
        this.mBoxPos = pIndex;
    }
    getBoxTargetCustom(pIdx) {
        for (let tPOIdata of this.mRoomElement.Params.CamPointTargetsCustom) {
            if (tPOIdata.index === pIdx) {
                return (tPOIdata);
            }
        }
        return (null);
    }
    setBoxTargetCustom(position) {
        let tPOIdata = this.getBoxTargetCustom(this.mBoxPos);
        if (tPOIdata != null) {
            if (position != null) {
                tPOIdata.target = position.clone();
                tPOIdata.camPosition = this.mRoomElement.Params.CamPathPoints[this.mBoxPos].clone();
                tPOIdata.viewAlongPath = false;
                tPOIdata.next_Target = true;
            }
            else {
                tPOIdata.target = this.mRoomElement.Params.CamPointTargetsCalculated[this.mBoxPos].target.clone();
                tPOIdata.camPosition = this.mRoomElement.Params.CamPathPoints[this.mBoxPos].clone();
            }
            this.computeTargets();
            return;
        }
        tPOIdata = {};
        tPOIdata.index = this.mBoxPos;
        tPOIdata.camPosition = this.mRoomElement.Params.CamPathPoints[this.mBoxPos].clone();
        if (position != null) {
            tPOIdata.target = position.clone();
        }
        else {
            tPOIdata.target = this.mRoomElement.Params.CamPointTargetsCalculated[this.mBoxPos].target.clone();
        }
        tPOIdata.targetHeight = this.mRoomElement.height;
        const tcurrViewAlongPath = this.currentViewAlongPath();
        tPOIdata.viewAlongPath = tcurrViewAlongPath;
        this.mRoomElement.Params.CamPointTargetsCustom.push(tPOIdata);
        this.mRoomElement.Params.CamPointTargetsCustom.sort((a, b) => (a.index > b.index) ? 1 : ((b.index > a.index) ? -1 : 0));
        const tnextTarget = this.getNextTargetCustom(this.mBoxPos);
        if (tnextTarget == null) {
            tPOIdata.next_Target = true;
        }
        else if (tnextTarget.viewAlongPath) {
            tPOIdata.next_Target = false;
        }
        else {
            tPOIdata.next_Target = true;
        }
        this.computeTargets();
        this.mRoomManager._addCamData();
    }
    getNextTargetCustom(pcurrIndex) {
        let ttmpPOI = null;
        if (this.mRoomElement.Params.CamPointTargetsCustom.length === 0) {
            return (null);
        }
        for (let tPOI of this.mRoomElement.Params.CamPointTargetsCustom) {
            if (tPOI.index === pcurrIndex) {
                ttmpPOI = tPOI;
                break;
            }
        }
        return (ttmpPOI);
    }
    _getNextTargetCustom(pcurrIndex) {
        let ttmpPOI = null;
        if (this.mRoomElement.Params.CamPointTargetsCustom.length === 0) {
            return (null);
        }
        for (let tPOI of this.mRoomElement.Params.CamPointTargetsCustom) {
            if (tPOI.index > pcurrIndex) {
                ttmpPOI = tPOI;
                break;
            }
        }
        return (ttmpPOI);
    }
    getPrevTargetCustom(pcurrIndex) {
        let ttmpPOI = null;
        if (this.mRoomElement.Params.CamPointTargetsCustom.length === 0) {
            return (null);
        }
        ttmpPOI = this.mRoomElement.Params.CamPointTargetsCustom[0];
        for (let tPOI of this.mRoomElement.Params.CamPointTargetsCustom) {
            if (tPOI.index < pcurrIndex) {
                ttmpPOI = tPOI;
                break;
            }
        }
        return (ttmpPOI);
    }
    getTargetIdx() {
        let tRes = [];
        this.mRoomElement.Params.CamPointTargetsCalculated = [];
        for (let tPOIdata of this.mRoomElement.Params.CamPointTargetsCustom) {
            this.mRoomElement.Params.CamPointTargetsCalculated[tPOIdata.index] = tPOIdata;
            tRes.push(tPOIdata.index);
        }
        return (tRes);
    }
    setDefaultTargets() {
        this.mRoomElement.Params.CamPointTargetsCalculated = [];
        for (let i = 0; i < this.mRoomElement.Params.CamPointTargets.length; i++) {
            let ttarget = this.mRoomElement.Params.CamPointTargets[i];
            const tPOIdata = {};
            tPOIdata.index = i;
            tPOIdata.target = ttarget;
            tPOIdata.viewAlongPath = true;
            this.mRoomElement.Params.CamPointTargetsCalculated.push(tPOIdata);
        }
    }
    deleteAllCustomTargets() {
        this.mRoomElement.Params.CamPointTargetsCustom = [];
        this.computeTargets();
        this.mRoomElement.notifyChange(ElementChangedFlags.Properties);
        this.mRoomElement.updateGeometry();
    }
    deleteCurrentCustomTarget() {
        for (let i = 0; i < this.mRoomElement.Params.CamPointTargetsCustom.length; i++) {
            const tPOIdata = this.mRoomElement.Params.CamPointTargetsCustom[i];
            if (tPOIdata.target == null) {
                continue;
            }
            if (tPOIdata.index === this.mBoxPos) {
                this.mRoomElement.Params.CamPointTargetsCustom.splice(i, 1);
                this.computeTargets();
                this.mRoomElement.notifyChange(ElementChangedFlags.Properties);
                this.mRoomElement.updateGeometry();
                return;
            }
        }
    }
    camIsOnCustomTarget() {
        let tRes = false;
        for (let tPOI of this.mRoomElement.Params.CamPointTargetsCustom) {
            if (tPOI.target == null) {
                continue;
            }
            if (tPOI.index === this.mBoxPos) {
                tRes = true;
                break;
            }
        }
        return (tRes);
    }
    calcTargetSteps(pStart, pEnd, pST, pET) {
        if (pST.viewAlongPath && pET.viewAlongPath) {
            for (let i = pStart; i < pEnd; i++) {
                const tPOIdata = {};
                tPOIdata.index = i;
                tPOIdata.target = this.mRoomElement.Params.CamPointTargets[i];
                tPOIdata.viewAlongPath = true;
                tPOIdata.next_Target = false;
                this.mRoomElement.Params.CamPointTargetsCalculated[i] = tPOIdata;
            }
            return;
        }
        const tDist = pEnd - pStart;
        const tVDist = Vector3.Distance(pST.target, pET.target) / (tDist - 1);
        let tstepsV = (pET.target.subtract(pST.target).normalize()).scale(tVDist);
        let tnext_target = false;
        if (!pET.viewAlongPath) {
            tnext_target = true;
        }
        for (let i = 0; i < tDist - 1; i++) {
            let tnextStep = pST.target.add(tstepsV.scale(i + 1));
            const tPOIdata = {};
            tPOIdata.index = pStart + (i + 1);
            tPOIdata.target = tnextStep;
            tPOIdata.viewAlongPath = false;
            tPOIdata.next_Target = tnext_target;
            this.mRoomElement.Params.CamPointTargetsCalculated[pStart + (i + 1)] = tPOIdata;
        }
    }
    computeMidPoints() {
        this.mMidPoints = [];
        let tcmPoints = this.mLinePoints;
        let tlastIdx = (tcmPoints.length - 1) - (this.mSplinePrecision * 0.5);
        for (let i = this.mSplinePrecision * 0.5; i <= tlastIdx; i += this.mSplinePrecision) {
            this.mMidPoints.push(tcmPoints[i]);
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    computeTargets() {
        const ttargetIdx = this.getTargetIdx();
        const tpathLength = this.mLinePoints.length - 1;
        if (ttargetIdx.length === 0) {
            this.setDefaultTargets();
        }
        else {
            // this.mRoomElement.Params.CamPointTargetsCalculated = [];
            const tcustTarg_first = this.getBoxTargetCustom(0);
            const tcustTarg_last = this.getBoxTargetCustom(tpathLength);
            if (tcustTarg_first == null) {
                const tPOIdata = {};
                tPOIdata.index = 0;
                tPOIdata.camPosition = this.mRoomElement.Params.CamPathPoints[0];
                tPOIdata.target = this.mRoomElement.Params.CamPointTargets[0].clone();
                tPOIdata.viewAlongPath = true;
                const tnextTarget = this._getNextTargetCustom(0);
                if (tnextTarget != null && !tnextTarget.viewAlongPath) {
                    tPOIdata.next_Target = true;
                }
                else {
                    tPOIdata.next_Target = false;
                }
                this.mRoomElement.Params.CamPointTargetsCalculated[0] = tPOIdata;
                ttargetIdx.unshift(0);
            }
            else {
                if (tcustTarg_first.camPosition === undefined) {
                    tcustTarg_first.camPosition = this.mRoomElement.Params.CamPathPoints[0];
                }
                this.mRoomElement.Params.CamPointTargetsCalculated[0] = tcustTarg_first;
            }
            if (tcustTarg_last == null) {
                const tlastIdx = ttargetIdx[ttargetIdx.length - 1];
                const tcustTarg_prevLast = this.getBoxTargetCustom(tlastIdx);
                if (tcustTarg_prevLast != null) {
                    const tPOIdataLast = {};
                    tPOIdataLast.index = tpathLength;
                    tPOIdataLast.viewAlongPath = tcustTarg_prevLast.viewAlongPath;
                    tPOIdataLast.camPosition = this.mRoomElement.Params.CamPathPoints[tpathLength];
                    if (tPOIdataLast.viewAlongPath) {
                        tPOIdataLast.target = this.mRoomElement.Params.CamPointTargets[tpathLength].clone();
                    }
                    else {
                        tPOIdataLast.target = tcustTarg_prevLast.target.clone();
                    }
                    tPOIdataLast.next_Target = !tPOIdataLast.viewAlongPath;
                    this.mRoomElement.Params.CamPointTargetsCalculated[tpathLength] = tPOIdataLast;
                    ttargetIdx.push(tpathLength);
                }
                else {
                    const tPOIdata = {};
                    tPOIdata.index = tpathLength;
                    tPOIdata.camPosition = this.mRoomElement.Params.CamPathPoints[tpathLength];
                    tPOIdata.target = this.mRoomElement.Params.CamPointTargets[tpathLength].clone();
                    tPOIdata.viewAlongPath = true;
                    tPOIdata.next_Target = false;
                    this.mRoomElement.Params.CamPointTargetsCalculated[tpathLength] = tPOIdata;
                    ttargetIdx.push(tpathLength);
                }
            }
            else {
                if (tcustTarg_last.camPosition === undefined) {
                    tcustTarg_last.camPosition = this.mRoomElement.Params.CamPathPoints[tpathLength];
                }
                this.mRoomElement.Params.CamPointTargetsCalculated[tpathLength] = tcustTarg_last;
            }
        }
        for (let i = 0; i < ttargetIdx.length - 1; i++) {
            const tIdx0 = ttargetIdx[i];
            const tIdx1 = ttargetIdx[i + 1];
            const tT0 = this.mRoomElement.Params.CamPointTargetsCalculated[tIdx0];
            const tT1 = this.mRoomElement.Params.CamPointTargetsCalculated[tIdx1];
            this.calcTargetSteps(tIdx0, tIdx1, tT0, tT1);
        }
    }
    correctDirection(pCreateNew) {
        if (pCreateNew)
            return;
        let tPoints = new Array();
        for (let tP of this.mPoints) {
            tPoints.push(tP);
        }
        if (!Util.vectorEquals3(tPoints[0], tPoints[tPoints.length - 1]))
            tPoints.push(tPoints[0]);
        let tClockwise = Util._clockWise3(tPoints);
        if (tClockwise && !pCreateNew) {
            this.mPoints.push(this.mPoints[0]);
            this.mPoints.reverse();
            this.mPoints.pop();
        }
    }
    correctDirection1() {
        let tClockwise = Util._clockWise3(this.mCutPoints);
        if (tClockwise)
            this.mCutPoints.reverse();
    }
    getInitialParams() {
        let tParams = {};
        tParams.width = 0.5;
        tParams.height = RoomManager.ROOM_CameraPathHeight;
        tParams.floorOffset = 0.0;
        tParams.color = this.mColor;
        return (tParams);
    }
    getparent() {
        return (this.mParent);
    }
    get Area() {
        return (this.mArea);
    }
    get Perimeter() {
        return (this.mPerimeter);
    }
    create2dGeometry(pParent) {
        this.mParent = pParent;
        const tcamPath = this.createCamPath(pParent, true);
        const tcamBox = this.createCamBox(pParent);
        const ttargetLines = this.createCustomTargetLines(pParent);
        const tcamView = this.createCamView(pParent);
        if (tcamPath != null) {
            this.mRoomElement.setMesh2d(tcamPath);
        }
    }
    create3dGeometry(pParent) {
        const tcamPath = this.createCamPath(pParent, false);
        const tcamBox = this.createCamBox(pParent);
        const ttargetLines = this.createCustomTargetLines(pParent);
        const tcamView = this.createCamView(pParent);
        if (tcamPath != null)
            this.mRoomElement.setMesh3d(tcamPath);
    }
    createSpecialGeometry(pParent) {
        //
    }
    createAdditionalGeometry(pParent) {
        //
    }
    // tslint:disable-next-line:cyclomatic-complexity
    getProperties() {
        let tProperties = new Array();
        let tYesNoChoices = new Array();
        for (let tYesNo of this.mRoomManager._YesNo)
            tYesNoChoices.push(this.mRoomManager._getYesNoPropValue(tYesNo));
        // path length
        let tcamPathLengthProp = new LengthProperty(this.mRoomElement, CameraPath.PROP_KEY_Length, this.mRoomManager.resources.propertyLength);
        tcamPathLengthProp.class = "base";
        tcamPathLengthProp.getValue = () => new PropertyValue(this.getPathLength());
        tcamPathLengthProp.editable = false;
        tProperties.push(tcamPathLengthProp);
        // path height
        let tcamPathHeightProp = new LengthProperty(this.mRoomElement, CameraPath.PROP_KEY_Height, this.mRoomManager.resources.propertyHeight);
        tcamPathHeightProp.class = "base";
        tcamPathHeightProp.getValue = () => new PropertyValue(this.mRoomElement.height);
        tcamPathHeightProp.setValue = (pValue) => {
            let tValue = LengthProperty.ConvertValueToNumber(pValue);
            if (tValue != null) {
                this.mRoomElement.setHeight(tValue);
                this.compute_DefaultPathTargets();
                return (Promise.resolve(PropertyChangedResult.Value));
            }
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        tProperties.push(tcamPathHeightProp);
        // // animation speed
        // let tanimationSpeedProp = new NumberProperty(this.mRoomElement, CameraPath.PROP_KEY_AnimationSpeed,
        //     this.mRoomManager.resources.propertyAnimationSpeed);
        // tanimationSpeedProp.class = "base";
        // tanimationSpeedProp.minValue = 1;
        // tanimationSpeedProp.maxValue = 50;
        // tanimationSpeedProp.getValue = () => new PropertyValue(this.mRoomElement.Params.animationSpeed);
        // tanimationSpeedProp.setValue = (pValue) => {
        //     let tValue = NumberProperty.ConvertValueToNumber(pValue);
        //     if (tValue != null) {
        //         this.setAnimationSpeed(tValue);
        //         return (Promise.resolve(PropertyChangedResult.Value));
        //     }
        //     return (Promise.resolve(PropertyChangedResult.Nothing));
        // };
        // tProperties.push(tanimationSpeedProp);
        // animation speed 1
        let tspeedTypeChoices = this.mRoomManager.SpeedTypePropList;
        const tanimation1Speed1Prop = new StringProperty(this.mRoomElement, CameraPath.PROP_KEY_AnimationSpeed, this.mRoomManager.resources.propertyAnimationSpeed);
        tanimation1Speed1Prop.class = "base";
        tanimation1Speed1Prop.getValue = () => this.getAnimationSpeed();
        tanimation1Speed1Prop.setValue = (pval) => {
            if (!pval)
                return Promise.resolve(PropertyChangedResult.Nothing);
            const tValue = StringProperty.ConvertValueToString(pval);
            if (tValue) {
                switch (tValue) {
                    case 'slow':
                        this.setAnimationSpeed(5);
                        break;
                    case 'medium':
                        this.setAnimationSpeed(8);
                        break;
                    case 'fast':
                        this.setAnimationSpeed(12);
                        break;
                    default:
                        this.setAnimationSpeed(5);
                }
                return Promise.resolve(PropertyChangedResult.Value);
            }
            return Promise.resolve(PropertyChangedResult.Nothing);
        };
        tanimation1Speed1Prop.choiceList = true;
        tanimation1Speed1Prop.getChoices = () => Promise.resolve(tspeedTypeChoices);
        tProperties.push(tanimation1Speed1Prop);
        // custom targets
        // tslint:disable-next-line:prefer-for-of
        let tindex = 1;
        for (let tPOIdata of this.mRoomElement.Params.CamPointTargetsCustom) {
            if (tPOIdata.target == null) {
                tindex++;
                continue;
            }
            if (tPOIdata.index === this.mBoxPos) {
                // current position
                let tcurrPosTargetProps = new BoolProperty(this.mRoomElement, CameraPath.PROP_KEY_CustomTargetCurrPos + '_' + tindex.toString(), "--- current ---");
                tcurrPosTargetProps.class = "POI" + "_" + tindex.toString();
                tcurrPosTargetProps.getValue = () => new PropertyValue(true);
                tcurrPosTargetProps.editable = false;
                tProperties.push(tcurrPosTargetProps);
            }
            let tviewAlongPathProps = new BoolProperty(this.mRoomElement, CameraPath.PROP_KEY_UsePathTarget + '_' + tindex.toString(), this.mRoomManager.resources.propertycameraViewAlongPath);
            tviewAlongPathProps.class = "POI" + "_" + tindex.toString();
            tviewAlongPathProps.choiceList = true;
            tviewAlongPathProps.getChoices = () => Promise.resolve(tYesNoChoices);
            tviewAlongPathProps.getValue = () => this.mRoomManager._getYesNoPropValue(tPOIdata.viewAlongPath);
            tviewAlongPathProps.setValue = (pValue) => {
                let tValue = BoolProperty.ConvertValueToBool(pValue);
                if (tValue != null) {
                    this.setViewAlongPath(tPOIdata.index, tValue);
                    return (Promise.resolve(PropertyChangedResult.Value));
                }
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            tProperties.push(tviewAlongPathProps);
            // target height
            let tcamTargetHeightProp = new LengthProperty(this.mRoomElement, CameraPath.PROP_KEY_CustomTargetHeight + '_' + tindex.toString(), this.mRoomManager.resources.propertycameraTargetHeight);
            tcamTargetHeightProp.class = "POI" + "_" + tindex.toString();
            tcamTargetHeightProp.getValue = () => new PropertyValue(tPOIdata.target.y);
            tcamTargetHeightProp.setValue = (pValue) => {
                let tValue = LengthProperty.ConvertValueToNumber(pValue);
                if (tValue != null) {
                    this.setTargetHeight(tPOIdata.index, tValue);
                    this.compute_DefaultPathTargets();
                    return (Promise.resolve(PropertyChangedResult.Value));
                }
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            tcamTargetHeightProp.visible = !tPOIdata.viewAlongPath;
            tProperties.push(tcamTargetHeightProp);
            tindex++;
        }
        return (tProperties);
    }
    serializeIOData() {
        return (this.mRoomElement.Params);
    }
    loadIOData(pData) {
        let tParams = pData;
        let tPoints = new Array();
        for (let tEP of tParams.ElementPoints) {
            let tP = new Vector3(tEP.x, tEP.y, tEP.z);
            tPoints.push(tP);
        }
        this.setPoints(tPoints);
    }
    setPoints(pPoints, pCreateNew = false) {
        this.mPoints = [];
        for (let tPoint of pPoints) {
            let tP = new Vector3(0, 0, 0);
            tP.x = tPoint.x;
            tP.y = tPoint.y;
            tP.z = tPoint.z;
            this.mPoints.push(tP);
        }
        if (this.mPoints.length > 3 && Util.vectorEquals3(this.mPoints[0], this.mPoints[this.mPoints.length - 1]))
            this.mPoints.pop();
        this.mRoomElement.Params.ElementPoints = this.mPoints;
        this.calcCenter();
        this.createCatMull_PathPoints();
        this.calcSize();
    }
    computeCamPathData(split = false) {
        this.createCatMull_PathPoints(split);
        this.compute_DefaultPathTargets();
        this.computeTargets();
        this.computeAnimationSpeed();
    }
    setAnimationSpeed(pAnimationSpeed) {
        let tspeed = clamp(pAnimationSpeed, 1, 50);
        this.mRoomElement.Params.animationSpeed = tspeed;
        this.computeAnimationSpeed();
        this.mRoomManager._addCamData();
        this.mRoomElement.notifyChange(ElementChangedFlags.PropertyValues);
    }
    setViewAlongPath(pIdx, pViewAlongPath) {
        for (let tPOIdata of this.mRoomElement.Params.CamPointTargetsCustom) {
            if (tPOIdata.index === pIdx) {
                tPOIdata.viewAlongPath = pViewAlongPath;
                tPOIdata.camPosition = this.mRoomElement.Params.CamPathPoints[pIdx].clone();
                tPOIdata.target = this.mRoomElement.Params.CamPointTargets[pIdx].clone();
                tPOIdata.targetHeight = tPOIdata.target.y;
                this.computeTargets();
                this.mRoomManager._addCamData();
                this.mRoomElement.notifyChange(ElementChangedFlags.Properties);
                this.mRoomElement.notifyChange(ElementChangedFlags.PropertyValues);
                this.mRoomElement.updateGeometry();
                return;
            }
        }
    }
    setTargetHeight(pIdx, pTargetHeight) {
        for (let tPOIdata of this.mRoomElement.Params.CamPointTargetsCustom) {
            if (tPOIdata.index === pIdx) {
                tPOIdata.targetHeight = pTargetHeight;
                tPOIdata.target.y = pTargetHeight;
                this.computeTargets();
                this.mRoomManager._addCamData();
                this.mRoomElement.notifyChange(ElementChangedFlags.PropertyValues);
                this.mRoomElement.updateGeometry();
                return;
            }
        }
    }
    computeAnimationSpeed() {
        const tPathLength = this.getPathLength();
        const tmInSec = tPathLength / (this.mRoomElement.Params.animationSpeed / 3.6);
        this.mDuration = tmInSec;
    }
    getPathLength() {
        const tPoints = this.mLinePoints;
        let tRes = 0;
        for (let i = 0; i < tPoints.length - 1; i++) {
            const tP0 = tPoints[i];
            const tP1 = tPoints[i + 1];
            const tDist = Vector3.Distance(tP0, tP1);
            tRes += tDist;
        }
        return (tRes);
    }
    deletePoint(pIndex) {
        if (pIndex > this.mPoints.length - 1 || pIndex < 0 || this.mPoints.length === 0 || this.mPoints.length <= 3)
            return;
        this.mPoints.splice(pIndex, 1);
        this.mRoomElement.Params.ElementPoints = this.mPoints;
        this.createCatMull_PathPoints();
        const tpathPointsLength = this.mLinePoints.length; // this.mCatMullRom.getPoints().length;
        if (this.mBoxPos > tpathPointsLength - 1) {
            this.mBoxPos = tpathPointsLength - 1;
        }
        this.calcCenter();
        this.calcSize();
    }
    addPoint(pIndex) {
        let tnewPoint = Vector3.Center(this.mPoints[pIndex], this.mPoints[pIndex + 1]);
        this.mPoints.splice(pIndex + 1, 0, tnewPoint);
        this.mRoomElement.Params.ElementPoints = this.mPoints;
        this.createCatMull_PathPoints();
        this.calcCenter();
        this.calcSize();
    }
    calcCenter() {
        let t2dPoints = new Array();
        for (let tP of this.mPoints)
            t2dPoints.push(new Vector2(tP.x, tP.z));
        let tCenter = new Vector3(0, 0, 0);
        if (this.mPoints.length > 2)
            tCenter = Util.getPolygonCentroid(t2dPoints);
        else {
            tCenter = Vector3.Center(this.mPoints[0], this.mPoints[1]);
        }
        this.mCenter = tCenter;
    }
    splitPathToEqualLength(pLineLength, pOffset) {
        if (pLineLength < 0.00001) {
            return;
        }
        this.mMidPoints = [];
        let tpointsTMP = this.mPoints.slice().reverse();
        tpointsTMP.pop();
        let tcompPoint = tpointsTMP.pop();
        let tmidIdx = 0;
        let tLinePoints_tmp = [];
        let tnextOffset = pOffset;
        let i = 1;
        for (i = 1; i <= this.mLinePoints.length - 2; i++) {
            const tP1 = this.mLinePoints[i - 1];
            const tP0 = this.mLinePoints[i];
            const tP2 = this.mLinePoints[i + 1];
            const tDir = (tP0.subtract(tP1)).normalize();
            let tP1offset = (tP1.add(tDir.scale(tnextOffset))).clone();
            const tL1_lenOffset = Vector3.Distance(tP1offset, tP0);
            const tnewSegCnt = Math.floor(tL1_lenOffset / pLineLength);
            const tremainder = tL1_lenOffset % pLineLength;
            let tprevPoint = tP1offset;
            tLinePoints_tmp.push(tprevPoint.clone());
            tmidIdx++;
            for (let j = 1; j <= tnewSegCnt; j++) {
                tprevPoint.addInPlace(tDir.scale(pLineLength));
                tLinePoints_tmp.push(tprevPoint.clone());
                tmidIdx++;
            }
            if (tcompPoint !== undefined &&
                Util.samePointsV3(tcompPoint, tP0, EPSILON)) {
                const tmidTMP = tLinePoints_tmp.length - (Math.floor(tmidIdx / 2));
                this.mMidPoints.push(tLinePoints_tmp[tmidTMP]);
                tmidIdx = 0;
                tcompPoint = tpointsTMP.pop();
            }
            if (tremainder > 0) {
                tP1offset = tprevPoint.clone();
                const tL1_len = Vector3.Distance(tP1offset, tP0);
                const tL3_len = pLineLength;
                const tL1 = tP1offset.subtract(tP0);
                const tL2 = tP2.subtract(tP0);
                const tgamma = angleBetweenVectors3(tL1, tL2);
                const talpha = Math.asin((Math.sin(tgamma) / tL3_len) * tL1_len);
                const tbeta = Math.PI - tgamma - talpha;
                const tL2_len = (pLineLength / Math.sin(tgamma)) * Math.sin(tbeta);
                tnextOffset = tL2_len;
            }
            else {
                tnextOffset = 0;
            }
        }
        const tlP1 = this.mLinePoints[i - 1];
        const tlP0 = this.mLinePoints[i];
        const tlDir = (tlP0.subtract(tlP1)).normalize();
        const tlprevPoint = (tlP1.add(tlDir.scale(tnextOffset))).clone();
        tLinePoints_tmp.push(tlprevPoint);
        tmidIdx++;
        tLinePoints_tmp.push(tlP0);
        const tlmidTMP = tLinePoints_tmp.length - (Math.floor(tmidIdx / 2));
        this.mMidPoints.push(tLinePoints_tmp[tlmidTMP]);
        this.mLinePoints = tLinePoints_tmp;
    }
    getAnimationSpeed() {
        const tslow = 5;
        const tmedium = 8;
        const tfast = 12;
        let tRes = new PropertyValue(this.mRoomManager.resources.propertySpeed_Slow);
        if (this.mRoomElement.Params.animationSpeed <= tslow) {
            tRes.value = 'slow';
            tRes.text = this.mRoomManager.resources.propertySpeed_Slow;
        }
        if (this.mRoomElement.Params.animationSpeed > tslow &&
            this.mRoomElement.Params.animationSpeed <= tmedium) {
            tRes.value = 'medium';
            tRes.text = this.mRoomManager.resources.propertySpeed_Medium;
        }
        if (this.mRoomElement.Params.animationSpeed > tmedium) {
            tRes.value = 'fast';
            tRes.text = this.mRoomManager.resources.propertySpeed_Fast;
        }
        return (tRes);
    }
    createCatMull_PathPoints(split = false) {
        if (this.mClosedPolygon) {
            let tPoints = new Array();
            for (let tP of this.mRoomElement.Params.ElementPoints) {
                tPoints.push(tP);
            }
            if ((tPoints.length > 3) &&
                (Util.vectorEquals3(tPoints[tPoints.length - 1], tPoints[tPoints.length - 2]))) {
                tPoints.pop();
            }
            this.mCatMullRom = Curve3.CreateCatmullRomSpline(tPoints, this.mSplinePrecision, false);
        }
        else {
            this.mCatMullRom = Curve3.CreateCatmullRomSpline(this.mRoomElement.Params.ElementPoints, this.mSplinePrecision, false);
            this.mLinePoints = this.mCatMullRom.getPoints();
            this.mLinePoints_ori = this.mLinePoints.slice();
            const tshortestLine = this.getShortestLineDistance();
            if (split) {
                this.splitPathToEqualLength(tshortestLine, 0);
            }
            else {
                this.computeMidPoints();
            }
            this.mRoomElement.Params.CamPathPoints = this.mLinePoints.slice();
        }
        this.calcSize();
        this.calcPerimeter();
    }
    compute_DefaultPathTargets() {
        if (this.mLinePoints == null ||
            this.mLinePoints.length < 2) {
            return;
        }
        this.mRoomElement.Params.CamPointTargets = [];
        let ttarget = Vector3.Zero();
        for (let i = 0; i < this.mLinePoints.length - 1; i++) {
            let tPi = this.mLinePoints[i].clone();
            tPi = Vector3.TransformCoordinates(tPi, this.mRoomElement.transform);
            // tPi.y = this.mRoomElement.height + tPi.y;
            let tPi1 = this.mLinePoints[i + 1].clone();
            tPi1 = Vector3.TransformCoordinates(tPi1, this.mRoomElement.transform);
            // tPi1.y = this.mRoomElement.height + tPi1.y;
            const tDir = (tPi1.subtract(tPi)).normalize();
            ttarget = tPi.add(tDir.scale(1));
            this.mRoomElement.Params.CamPointTargets.push(ttarget);
        }
        this.mRoomElement.Params.CamPointTargets.push(ttarget);
    }
    calcPerimeter() {
        let tPoints = this.mLinePoints;
        let tDist = 0;
        for (let i = 1; i < tPoints.length; i++) {
            tDist += Vector3.Distance(tPoints[i - 1], tPoints[i]);
        }
        this.mPerimeter = tDist;
    }
    calcSize() {
        let tPoints = this.mLinePoints;
        if (tPoints.length === 0)
            return;
        let tminX = tPoints[0].x;
        let tminY = tPoints[0].z;
        let tmaxX = tPoints[0].x;
        let tmaxY = tPoints[0].z;
        for (let tPoint of tPoints) {
            tminX = Math.min(tPoint.x, tminX);
            tmaxX = Math.max(tPoint.x, tmaxX);
            tminY = Math.min(tPoint.z, tminY);
            tmaxY = Math.max(tPoint.z, tmaxY);
        }
        this.mWidth = tmaxX - tminX;
        this.mDepth = tmaxY - tminY;
        this.mRoomElement.setWidth(this.mWidth, false, false);
        this.mRoomElement.setDepth(this.mDepth, false, false);
        this.mminPoint = new Vector3(tminX, 0, tminY);
        this.mmaxPoint = new Vector3(tmaxX, 0, tmaxY);
    }
    drawCamPath_Positions(pParent, p2d = false, ponTop = false) {
        // for (let i = 0; i < this.mLinePoints.length - 1; i++) {
        //     const tP0 = this.mLinePoints[i];
        //     const tP1 = this.mLinePoints[i + 1];
        //     let tcatmullRomCamPath = Mesh.CreateLines("room_cameraAnimation", [tP0, tP1], this.mScene);
        //     if (Math.abs(i % 2) === 1) {
        //         tcatmullRomCamPath.color = Color3.FromHexString("#FF0000");
        //     } else {
        //         tcatmullRomCamPath.color = Color3.FromHexString("#00FF00");
        //     }
        //     tcatmullRomCamPath.parent = pParent;
        //     tcatmullRomCamPath.isPickable = false;
        //     ShadowGenerator.disableMeshShadow(tcatmullRomCamPath);
        //     tcatmullRomCamPath.renderingGroupId = 1;
        //     this.mRoomElement.Params.MeshVertices = tcatmullRomCamPath.getVerticesData(VertexBuffer.PositionKind);
        //     if (p2d) {
        //         this.prepare2dMesh(tcatmullRomCamPath);
        //     } else {
        //         this.prepare3dMesh(tcatmullRomCamPath);
        //     }
        // }
        // for (let i = 0; i < this.mLinePoints_ori.length - 1; i++) {
        //     const tP0 = this.mLinePoints_ori[i];
        //     const tP1 = this.mLinePoints_ori[i + 1];
        //     let tcatmullRomCamPath = Mesh.CreateLines("room_cameraAnimation", [tP0, tP1], this.mScene);
        //     if (Math.abs(i % 2) === 1) {
        //         tcatmullRomCamPath.color = Color3.FromHexString("#000000");
        //     } else {
        //         tcatmullRomCamPath.color = Color3.FromHexString("#0000FF");
        //     }
        //     tcatmullRomCamPath.parent = pParent;
        //     tcatmullRomCamPath.isPickable = false;
        //     ShadowGenerator.disableMeshShadow(tcatmullRomCamPath);
        //     tcatmullRomCamPath.renderingGroupId = 1;
        //     this.mRoomElement.Params.MeshVertices = tcatmullRomCamPath.getVerticesData(VertexBuffer.PositionKind);
        //     if (p2d) {
        //         this.prepare2dMesh(tcatmullRomCamPath);
        //     } else {
        //         this.prepare3dMesh(tcatmullRomCamPath);
        //     }
        // }
        let tcatmullRomCamPath1 = CreateLines("room_cameraAnimation", { points: this.mLinePoints }, this.mScene);
        tcatmullRomCamPath1.color = Color3.FromHexString("#ff0000");
        tcatmullRomCamPath1.parent = pParent;
        tcatmullRomCamPath1.isPickable = false;
        ShadowGenerator.disableMeshShadow(tcatmullRomCamPath1);
        tcatmullRomCamPath1.renderingGroupId = 1;
        this.mRoomElement.Params.MeshVertices = tcatmullRomCamPath1.getVerticesData(VertexBuffer.PositionKind);
        if (p2d) {
            this.prepare2dMesh(tcatmullRomCamPath1);
        }
        else {
            this.prepare3dMesh(tcatmullRomCamPath1);
        }
        const tselectTube = CreateTube("room_cameraAnimationInvSelTube", { path: this.mLinePoints,
            radius: 0.2,
            tessellation: 10,
            sideOrientation: Mesh.DOUBLESIDE }, this.mScene);
        tselectTube.name = "pickingMesh_CamPath";
        tselectTube.isPickable = true;
        tselectTube.parent = pParent;
        tselectTube.visibility = 0;
        ShadowGenerator.disableMeshShadow(tselectTube);
        if (p2d) {
            this.prepare2dMesh(tselectTube);
        }
        else {
            this.prepare3dMesh(tselectTube);
        }
        return (tselectTube);
    }
    createCamPath(pParent, p2d = false) {
        if (this.mPoints.length === 0)
            return (null);
        let tMesh = this.drawCamPath_Positions(pParent, p2d);
        return (tMesh);
    }
    createCamBox(pParent) {
        if (this.mLinePoints.length < 2) {
            return (null);
        }
        const tx = new Vector2(1, 0);
        const tL1 = Util.to2d(this.mLinePoints[1].subtract(this.mLinePoints[0]));
        const tcamPos = this.mLinePoints;
        let tMesh = CreateBox('room_camBox', { width: 0.15, depth: 0.25, height: 0.15 }, this.mScene);
        ShadowGenerator.disableMeshShadow(tMesh);
        tMesh.parent = pParent;
        tMesh.position = this.getBoxPos().clone();
        // tMesh.position.y = 0;
        tMesh.position.y = tcamPos[this.mBoxPos].y;
        const tangle = signedAngleBetweenVectors2(tx, tL1);
        //tMesh.rotation = new Vector3(0, -tangle, 0);
        tMesh.material = this.getDarkGrayPlasticMaterial();
        const plane = CreatePlane("plane", { height: 0.05, width: 0.17719298, sideOrientation: Mesh.DOUBLESIDE });
        plane.material = this.mRoomManager.camLogo;
        plane.parent = tMesh;
        plane.position.x = -0.0751;
        plane.rotation.y = Math.PI / 2;
        const plane1 = CreatePlane("plane", { height: 0.05, width: 0.17719298, sideOrientation: Mesh.DOUBLESIDE });
        plane1.material = this.mRoomManager.camLogo;
        plane1.parent = tMesh;
        plane1.scaling.x = -1;
        plane1.position.x = 0.0751;
        plane1.rotation.y = Math.PI / 2;
        const tcamLense = CreateCylinder("cylinder", {
            diameterTop: 0.1,
            diameterBottom: 0.2,
            height: 0.070
        }, this.mScene);
        tcamLense.parent = tMesh;
        tcamLense.material = this.getDarkGrayPlasticMaterial();
        tcamLense.rotation.x = -(Math.PI / 2);
        tcamLense.position.z = 0.16;
        const tfilmReel1 = CreateCylinder("cylinder", {
            diameterTop: 0.20,
            diameterBottom: 0.20,
            height: 0.08
        }, this.mScene);
        tfilmReel1.parent = tMesh;
        tfilmReel1.material = this.getDarkGrayPlasticMaterial();
        tfilmReel1.rotation.z = -(Math.PI / 2);
        tfilmReel1.position.z = -0.05;
        tfilmReel1.position.y = 0.15;
        const tfilmReel2 = CreateCylinder("cylinder", {
            diameterTop: 0.20,
            diameterBottom: 0.20,
            height: 0.08
        }, this.mScene);
        tfilmReel2.parent = tMesh;
        tfilmReel2.material = this.getDarkGrayPlasticMaterial();
        tfilmReel2.rotation.z = -(Math.PI / 2);
        tfilmReel2.position.z = -0.20;
        tfilmReel2.position.y = 0;
        if (this.mRoomElement.Params.CamPointTargets != null &&
            this.mRoomElement.Params.CamPointTargets.length > 0) {
            let tcamTarget = this.mRoomElement.Params.CamPointTargetsCalculated[this.mBoxPos];
            const tlookAt = tcamTarget.target.subtract(this.mRoomElement.node.position);
            // tlookat.target.y = 0;
            tMesh.lookAt(tlookAt);
        }
        return (tMesh);
    }
    createCamView(pParent) {
        if (this.mLinePoints.length < 2 || this.mRoomElement.Params.CamPointTargetsCalculated == null)
            return (null);
        let tHelpLines = new Array();
        const tnextCustomTarget = this.nextViewAlongPath();
        let tlineColor = "#95C11F";
        if (tnextCustomTarget == null ||
            (tnextCustomTarget != null && !tnextCustomTarget)) {
            tlineColor = "#b2b2b2";
        }
        if (this.mRoomManager.interactorState_CameraPath != InteractorMode.Target) {
            tlineColor = "#b2b2b2";
        }
        let tcurrPos = this.getBoxPos();
        let tcurrTarget = this.mRoomElement.Params.CamPointTargetsCalculated[this.mBoxPos];
        const tcurrTargetTrans = tcurrTarget.target.subtract(this.mRoomElement.node.position);
        tHelpLines.push([tcurrPos, tcurrTargetTrans]);
        let tLines = CreateLineSystem("shape_helplines", { lines: tHelpLines }, this.mScene);
        tLines.parent = pParent;
        tLines.renderingGroupId = 1;
        this.mRoomManager.app.model.layers.setMeshLayer(tLines, "room:ShapeHelpLines");
        tLines.color = Color3.FromHexString(tlineColor);
        ShadowGenerator.disableMeshShadow(tLines);
        return (tLines);
    }
    createCustomTargetLines(pParent) {
        if (this.mRoomManager.interactorState_CameraPath != InteractorMode.Target) {
            return (null);
        }
        const tcurrPos = this.getBoxPos();
        const ttargetLines = new Array();
        for (const tPOI of this.mRoomElement.Params.CamPointTargetsCustom) {
            if (tPOI.viewAlongPath) {
                continue;
            }
            let ttargetCamPos = tPOI.camPosition;
            let tpathPos = this.mRoomElement.Params.CamPathPoints[tPOI.index].clone();
            if (Util.vectorEquals3(tpathPos, tcurrPos)) {
                continue;
            }
            ttargetCamPos.y = tpathPos.y;
            const ttargetPos = tPOI.target.subtract(this.mRoomElement.node.position);
            ttargetLines.push([tpathPos, ttargetPos]);
        }
        if (ttargetLines.length == 0) {
            return (null);
        }
        const tlineColor = "#bbbbbb";
        const tLines = CreateLineSystem("camTargetLines", { lines: ttargetLines }, this.mScene);
        tLines.parent = pParent;
        tLines.renderingGroupId = 1;
        this.mRoomManager.app.model.layers.setMeshLayer(tLines, "room:ShapeHelpLines");
        tLines.color = Color3.FromHexString(tlineColor);
        ShadowGenerator.disableMeshShadow(tLines);
        return (tLines);
    }
}
CameraPath.splitPath = false;
//// private ///////////////////////////////////////////////////////////////////////////////
CameraPath.PROP_KEY_Height = "room:RoomElement:camHeight";
CameraPath.PROP_KEY_Length = "room:RoomElement:camPathLength";
CameraPath.PROP_KEY_AnimationSpeed = "room:RoomElement:animationSpeed";
CameraPath.PROP_KEY_CustomTargetHeight = "room:RoomElement:camCustomTargetHeight";
CameraPath.PROP_KEY_CustomTargetCurrPos = "room:RoomElement:camCustomTargetCurrPos";
CameraPath.PROP_KEY_UsePathTarget = "room:RoomElement:camUsePathTarget";
